import { any, number, optional, optionalBoolean, optionalNumber, optionalString, string, zodLess, ZodLessInfer } from '@kaiizen/zod-less';
import { compare } from 'fast-json-patch';
import { z } from 'zod';
import { ScanGroupSchema, ScanLineItemSchema, ScanOptionSchema, ScanRootSchema, ScanTaskSchema } from '../ScanRoot';
import { ScanTemplatePartOverrideSchema, ScanTemplatePartSchema } from '../ScanTemplateRoot';

const SavedScanLineItemDataSchema = ScanLineItemSchema.omit({ id: true, updatedAt: true, createdAt: true, templateId: true });
const SavedScanOptionDataSchema = ScanOptionSchema.omit({ id: true, updatedAt: true, createdAt: true, templateId: true });
const SavedScanTaskDataSchema = ScanTaskSchema.omit({ id: true, updatedAt: true, createdAt: true, templateId: true });
const SavedScanGroupDataSchema = ScanGroupSchema.omit({ id: true, updatedAt: true, createdAt: true, templateId: true });
const SavedScanRootDataSchema = ScanRootSchema.omit({ id: true, updatedAt: true, createdAt: true, templateId: true });
const SavedScanTemplatePartDataSchema = ScanTemplatePartSchema.omit({ id: true, updatedAt: true, overrides: true, createdAt: true });
const SavedScanTemplateGroupDataSchema = SavedScanTemplatePartDataSchema.extend({ taskTemplateIds: optional([string]) });
const SavedScanTemplateOverrideSchema = ScanTemplatePartOverrideSchema.omit({ id: true });

export type SavedScanRootData = ZodLessInfer<typeof SavedScanRootDataSchema>;

export const ScanDataInMemoryCommonSchema = zodLess({
  // identity
  id: string, // version id - every version has a unique id - this is only used as the DB-id, the business and client logic all use scanPartID & updatedAt to identify the version
  updatedAt: number, // Timestamp of this version - every version has a unique timestamp
  scanPartId: string, // logical identifier shared across versions: e.g. groupId, taskId, lineItemId, groupTemplateId, etc.

  // foreign keys
  scanId: optionalString, // Foreign key to Scan (if part of a scan)
  scanTemplateId: optionalString, // Foreign key to ScanTemplate (if part of a template)
  templateScanPartId: optionalString, // only for ScanPart ScanData records: the scanPartId of the template node that was used to create this scan object

  // overrides - each scan can select one or more overrides by overrideType which get merged into the parent TemplatePart's data (parent identifies its child overrides via childrenScanPartIds)
  overrideType: optionalString, // If this is type == "overrideTemplate", this identifies the override type. It can be an ID (e.g. a marketId) or any other unique string (e.g, "bronze-tier").

  // additional versioning data
  dataDeltaPatch: optional([{ op: string, path: string, value: any.optional() }]), // json patch standard (RFC 6902) schema about what changed from previous version
  basedOnLastUpdatedAt: optionalNumber, // Timestamp of the version this was based on (used to detect potential write conflicts)
  deleted: optionalBoolean, // Marks this version as deleted (only the version when it was deleted needs to be marked)

  // versioned data
  childrenScanPartIds: optional([string]), // child ScanTemplateParts (Groups, Tasks, Options, LineItems, *including Overrides*)
  // and "data" - but data schemas are defined below in the discriminatedUnion
});

// Create discriminated union based on type field
const ScanDataInMemoryWithDiscriminatedData = z.discriminatedUnion('type', [
  // scan types
  ScanDataInMemoryCommonSchema.extend({ type: zodLess('scan'), data: optional(SavedScanRootDataSchema) }),
  ScanDataInMemoryCommonSchema.extend({ type: zodLess('group'), data: optional(SavedScanGroupDataSchema) }),
  ScanDataInMemoryCommonSchema.extend({ type: zodLess('task'), data: optional(SavedScanTaskDataSchema) }),
  ScanDataInMemoryCommonSchema.extend({ type: zodLess('option'), data: optional(SavedScanOptionDataSchema) }),
  ScanDataInMemoryCommonSchema.extend({ type: zodLess('lineItem'), data: optional(SavedScanLineItemDataSchema) }),

  // scan template types
  ScanDataInMemoryCommonSchema.extend({
    type: zodLess('scanTemplate|taskTemplate|optionTemplate|lineItemTemplate'),
    data: optional(SavedScanTemplatePartDataSchema),
  }),

  ScanDataInMemoryCommonSchema.extend({
    type: zodLess('groupTemplate'),
    data: optional(SavedScanTemplateGroupDataSchema),
  }),

  // overrideTemplate: SavedScanTemplateOverrideSchema
  ScanDataInMemoryCommonSchema.extend({
    type: zodLess('overrideTemplate'),
    data: optional(SavedScanTemplateOverrideSchema),
  }),
]).refine(data => {
  return !!data.scanId !== !!data.scanTemplateId;
}, {
  message: 'Must have exactly one of scanId or scanTemplateId, but not both.',
  path: ['scanDataInMemory'],
});

export const ScanDataInMemorySchema = ScanDataInMemoryWithDiscriminatedData;
export type ScanDataInMemory = ZodLessInfer<typeof ScanDataInMemorySchema>;

export const isScanDataInMemoryRoot = (scanDataInMemory: ScanDataInMemory): boolean =>
  scanDataInMemory.type === 'scan' || scanDataInMemory.type === 'scanTemplate';

/**
 * return ONLY the versioned data from a ScanDataInMemory object
 * @param scanDataInMemory
 * @returns
 */
export const getVersionedDataFromScanDataInMemory = (scanDataInMemory: ScanDataInMemory): { childrenScanPartIds: string[] | undefined; data: any } => {
  return {
    childrenScanPartIds: scanDataInMemory.childrenScanPartIds,
    data: scanDataInMemory.data,
  };
};

export const getVersionedDataDeltaPatch = (before: ScanDataInMemory, after: ScanDataInMemory): any =>
  compare(
    getVersionedDataFromScanDataInMemory(before),
    getVersionedDataFromScanDataInMemory(after),
  );
